import { Stack, useMediaQuery } from '@mui/material';
import { useEffect, useMemo } from 'react';

import { useBillsQuery } from '../../../api/getBills';
import { usePoliciesQuery } from '../../../api/getPolicies';
import { getBillsMarketingCards } from '../../../components/InfoCards';
import PayAllPoliciesCard from '../../../components/InfoCards/PayAllPoliciesCard';
import PhoneNumberLink from '../../../components/styledComponents/PhoneNumberLink';
import StatusAlert from '../../../components/styledComponents/StatusAlert';
import { StatusType } from '../../../constants/enums';
import useUpdateTitle from '../../../hooks/useUpdateTitle';
import { useLastPayment } from '../../../providers/LastPaymentProvider';
import { useSnackbarsContext } from '../../../store/SnackbarProvider';
import theme from '../../../styles/theme';
import BillCard from './BillCard';
import CircularProgress from '../../../components/CircularProgress/CircularProgress';

export interface BillsCardProps {
  ubiEnabled?: boolean;
  unenrolledPaperlessPolicy?: boolean;
}

const BillCards = ({
  ubiEnabled,
  unenrolledPaperlessPolicy,
}: BillsCardProps) => {
  useUpdateTitle('Bills');
  const { isLoading, data, error } = useBillsQuery();
  const isMobile = useMediaQuery(theme.breakpoints.down('desktop'));
  const { enqueueSnackbar, closeSnackbar } = useSnackbarsContext();
  const bills = data?.payments;
  const { data: policyData } = usePoliciesQuery();
  const crossSellInfo = policyData?.crossSellInfo;
  const showAutoHomeCard = crossSellInfo?.crossSellEligible && !!bills;
  const { showPaymentDelayBanner } = useLastPayment();

  useEffect(() => {
    const isPastDue =
      bills && bills?.filter((bill) => bill?.pastDue)?.length > 0;

    if (isPastDue) {
      enqueueSnackbar({
        dataTestId: 'past-due-alert',
        message: 'Payment is past due',
        variant: 'info',
        key: 'pastDue',
      });
    }
  }, [bills]);

  useEffect(
    () => () => {
      closeSnackbar('pastDue');
    },
    []
  );

  const multipleBillsDue = useMemo(
    () =>
      bills &&
      bills?.filter(
        (bill) =>
          bill &&
          (parseFloat(bill.minimumDueAmount) > 0 ||
            parseFloat(bill.totalDueAmount) > 0)
      ).length > 1,
    [bills]
  );

  const mobileCards = bills?.flatMap((e, idx) => {
    const BillsMarketingCards = getBillsMarketingCards(
      ubiEnabled,
      unenrolledPaperlessPolicy,
      showAutoHomeCard
    );
    const MarketingCard = BillsMarketingCards[idx] ?? null;

    if (idx < BillsMarketingCards.length && idx >= bills.length - 1) {
      return [
        <MarketingCard
          key={`bills-marketing-${idx}`}
          data-testid={`bills-marketing-${idx}`}
        />,
        <BillCard
          key={`bill-${idx}-${e.policyNumber}`}
          bill={e}
          data-testid={`bill-card-${e.policyNumber}`}
        />,

        BillsMarketingCards.slice(idx + 1).map((BillsMarketingCard, index) => (
          <BillsMarketingCard
            key={`bills-marketing-${index}`}
            data-testid={`bills-marketing-${index}`}
          />
        )),
      ];
    } else {
      return BillsMarketingCards[idx]
        ? [
            <MarketingCard
              key={`bills-marketing-${idx}`}
              data-testid={`bills-marketing-${idx}`}
            />,
            <BillCard
              key={`bill-${idx}-${e.policyNumber}`}
              bill={e}
              data-testid={`bill-card-${e.policyNumber}`}
            />,
          ]
        : [
            <BillCard
              key={`bill-${idx}-${e.policyNumber}`}
              bill={e}
              data-testid={`bill-card-${e.policyNumber}`}
            />,
          ];
    }
  });

  if (isLoading) {
    return (
      <Stack
        sx={{
          alignItems: "center",
          justifyContent: "center",
          width: 1
        }}>
        <CircularProgress size={32} />
      </Stack>
    );
  }

  if (error) {
    return (
      <StatusAlert
        key="bills-load-error"
        open
        title="This information is currently unavailable."
        text={
          <>
            Try again later or call &nbsp;
            <PhoneNumberLink phoneNumber="8889805650" />.
          </>
        }
        status={StatusType.ERROR}
      />
    );
  }

  return (
    <>
      {multipleBillsDue && <PayAllPoliciesCard />}
      {showPaymentDelayBanner && (
        <StatusAlert
          key="payment-delay-banner"
          open
          text="It may take up to 48 hours before your account reflects the payment."
          status={StatusType.INFO}
        />
      )}
      {isMobile
        ? mobileCards
        : bills?.map((bill, idx) => (
            <BillCard
              key={`bill-${idx}-${bill.policyNumber}`}
              bill={bill}
              data-testid={`bill-card-${bill.policyNumber}`}
            />
          ))}
    </>
  );
};

export default BillCards;
